import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { User } from '../../services/authService';
import { NovelService, Chapter } from '../../services/novelService';
import PageHeader from '../common/PageHeader';
import ChapterEditor from './ChapterEditor';
import './ChapterEditorPage.css';

interface ChapterEditorPageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const ChapterEditorPage: React.FC<ChapterEditorPageProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId, chapterId } = useParams<{ novelId: string; chapterId: string }>();
  const navigate = useNavigate();
  
  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track current content being edited (may differ from saved chapter.content)
  const [currentContent, setCurrentContent] = useState<string>('');
  
  // Sync status state
  const [syncStatus, setSyncStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  
  // View mode state
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  
  // Dropdown menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (novelId && chapterId) {
      loadChapter();
    }
  }, [novelId, chapterId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Update sync status display every 10 seconds
  useEffect(() => {
    if (lastSyncTime && syncStatus === 'saved') {
      const interval = setInterval(() => {
        // Force re-render to update time display
        setLastSyncTime(new Date(lastSyncTime));
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [lastSyncTime, syncStatus]);

  const loadChapter = async () => {
    if (!novelId || !chapterId) return;

    try {
      setLoading(true);
      setError(null);
      
      // Load current chapter
      const data = await NovelService.getChapter(novelId, chapterId);
      setChapter(data);
      setCurrentContent(data.content?.text_value || '');
    } catch (err) {
      console.error('Error loading chapter:', err);
      setError('Failed to load chapter');
    } finally {
      setLoading(false);
    }
  };

  const handleChapterContentUpdate = async (chapterId: string, content: string) => {
    if (!novelId) return;

    // Update current content immediately (for AI continuation)
    setCurrentContent(content);

    try {
      setSyncStatus('saving');
      await NovelService.updateChapter(novelId, chapterId, {
        content
      });
      setSyncStatus('saved');
      setLastSyncTime(new Date());
    } catch (err) {
      console.error('Error updating chapter content:', err);
      setSyncStatus('error');
      setError('Failed to save chapter content');
    }
  };

  const handleBack = () => {
    navigate(`/novel/${novelId}`);
  };

  const handleAIWriter = () => {
    navigate(`/novel/${novelId}/chapter/${chapterId}/ai-writer`);
  };

  const handleMetadata = () => {
    setIsMenuOpen(false);
    navigate(`/novel/${novelId}/metadata`);
  };

  const handleToggleViewMode = () => {
    setIsMenuOpen(false);
    setViewMode(viewMode === 'edit' ? 'preview' : 'edit');
  };

  const handleAIWriterClick = () => {
    setIsMenuOpen(false);
    handleAIWriter();
  };

  const handleCopyContent = async () => {
    setIsMenuOpen(false);
    try {
      await navigator.clipboard.writeText(currentContent);
      // You could add a toast notification here if you have one
      alert('Chapter content copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy content:', err);
      alert('Failed to copy content to clipboard');
    }
  };

  const getSyncStatusText = () => {
    if (syncStatus === 'saving') {
      return 'Saving...';
    } else if (syncStatus === 'error') {
      return 'Save failed';
    } else if (lastSyncTime) {
      const now = new Date();
      const diffMs = now.getTime() - lastSyncTime.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      
      if (diffSecs < 5) {
        return 'Saved just now';
      } else if (diffSecs < 60) {
        return `Saved ${diffSecs}s ago`;
      } else if (diffSecs < 3600) {
        const mins = Math.floor(diffSecs / 60);
        return `Saved ${mins}m ago`;
      } else {
        const hours = Math.floor(diffSecs / 3600);
        return `Saved ${hours}h ago`;
      }
    }
    return 'Saved';
  };

  if (loading) {
    return (
      <div className="chapter-editor-page-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
          rightActions={
            <button 
              className="chapter-editor-close-btn"
              onClick={handleBack}
            >
              ✕
            </button>
          }
        />
        <div className="chapter-editor-page-loading">Loading chapter...</div>
      </div>
    );
  }

  if (error || !chapter) {
    return (
      <div className="chapter-editor-page-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
          rightActions={
            <button 
              className="chapter-editor-close-btn"
              onClick={handleBack}
            >
              ✕
            </button>
          }
        />
        <div className="chapter-editor-page-error">
          {error || 'Chapter not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="chapter-editor-page-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="chapter-editor-header-center">
            <h1 className="page-header-center-title">{chapter.title}</h1>
            <span className={`chapter-sync-status ${syncStatus}`}>
              {syncStatus === 'saving' && '⟳'}
              {syncStatus === 'saved' && '✓'}
              {syncStatus === 'error' && '✕'}
              {' '}
              {getSyncStatusText()}
            </span>
          </div>
        }
        leftActions={
          <button 
            className="chapter-back-btn"
            onClick={handleBack}
          >
            ← Back
          </button>
        }
        rightActions={
          <div className="chapter-editor-right-actions" ref={menuRef}>
            <button 
              className="chapter-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              ⋮
            </button>
            {isMenuOpen && (
              <div className="chapter-menu-dropdown">
                <button 
                  className="chapter-menu-item"
                  onClick={handleToggleViewMode}
                >
                  {viewMode === 'edit' ? '👁️ Preview' : '✏️ Edit'}
                </button>
                <button 
                  className="chapter-menu-item"
                  onClick={handleCopyContent}
                >
                  📋 Copy
                </button>
                <button 
                  className="chapter-menu-item"
                  onClick={handleAIWriterClick}
                >
                  AI Writer
                </button>
                <button 
                  className="chapter-menu-item"
                  onClick={handleMetadata}
                >
                  Metadata
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className="chapter-editor-page-content">
        {viewMode === 'edit' ? (
          <ChapterEditor
            chapter={chapter}
            onContentUpdate={handleChapterContentUpdate}
          />
        ) : (
          <div className="chapter-preview-container">
            <ReactMarkdown
              components={{
                img: ({ node, ...props }) => (
                  <img 
                    {...props} 
                    className="chapter-preview-image"
                    alt={props.alt || ''} 
                  />
                )
              }}
            >
              {currentContent || 'No content yet'}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChapterEditorPage;

